'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

// Professional SVG preview thumbnails
function RestaurantThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#1a1008"/>
      <rect x="8" y="8" width="64" height="40" rx="4" fill="#2a1a0a"/>
      <rect x="12" y="12" width="56" height="6" rx="2" fill="#D4AF37" opacity="0.8"/>
      <rect x="12" y="22" width="24" height="3" rx="1" fill="#fff" opacity="0.6"/>
      <rect x="12" y="27" width="18" height="2" rx="1" fill="#fff" opacity="0.3"/>
      <rect x="40" y="22" width="24" height="3" rx="1" fill="#fff" opacity="0.6"/>
      <rect x="40" y="27" width="18" height="2" rx="1" fill="#fff" opacity="0.3"/>
      <rect x="12" y="34" width="56" height="1" rx="0.5" fill="#D4AF37" opacity="0.3"/>
      <rect x="12" y="38" width="20" height="2" rx="1" fill="#D4AF37" opacity="0.5"/>
      <rect x="12" y="54" width="56" height="18" rx="4" fill="#2a1a0a"/>
      <rect x="16" y="58" width="20" height="10" rx="2" fill="#D4AF37" opacity="0.15"/>
      <rect x="40" y="58" width="20" height="10" rx="2" fill="#D4AF37" opacity="0.15"/>
      <rect x="18" y="60" width="12" height="2" rx="1" fill="#fff" opacity="0.5"/>
      <rect x="18" y="63" width="8" height="1.5" rx="0.75" fill="#D4AF37" opacity="0.6"/>
    </svg>
  );
}

function NailStudioThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#1a0a14"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#2a0f1e"/>
      <circle cx="40" cy="28" r="14" fill="#F472B6" opacity="0.25"/>
      <circle cx="40" cy="28" r="10" fill="#F472B6" opacity="0.4"/>
      <circle cx="40" cy="28" r="6" fill="#F472B6" opacity="0.7"/>
      <rect x="16" y="48" width="48" height="3" rx="1.5" fill="#F472B6" opacity="0.6"/>
      <rect x="22" y="54" width="36" height="2" rx="1" fill="#fff" opacity="0.4"/>
      <rect x="28" y="59" width="24" height="6" rx="3" fill="#D4AF37" opacity="0.7"/>
      <rect x="32" y="61" width="16" height="2" rx="1" fill="#fff" opacity="0.8"/>
    </svg>
  );
}

function HairSalonThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#0f0a0a"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#1a0f0f"/>
      <rect x="8" y="8" width="64" height="35" rx="6" fill="#2a1515"/>
      <ellipse cx="40" cy="26" rx="16" ry="18" fill="#8B4513" opacity="0.3"/>
      <ellipse cx="40" cy="22" rx="10" ry="12" fill="#D2691E" opacity="0.4"/>
      <rect x="12" y="48" width="56" height="3" rx="1.5" fill="#fff" opacity="0.7"/>
      <rect x="16" y="54" width="48" height="2" rx="1" fill="#fff" opacity="0.4"/>
      <rect x="24" y="59" width="32" height="6" rx="3" fill="#D4AF37" opacity="0.8"/>
      <rect x="28" y="61" width="24" height="2" rx="1" fill="#fff" opacity="0.9"/>
    </svg>
  );
}

function RealEstateThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#080e1a"/>
      <rect x="8" y="8" width="64" height="40" rx="4" fill="#0f1e2a"/>
      <rect x="12" y="12" width="56" height="32" rx="3" fill="#1a2a3a"/>
      <rect x="16" y="16" width="48" height="20" rx="2" fill="#2a3a4a"/>
      <polygon points="40,18 52,26 52,36 28,36 28,26" fill="#3a4a5a"/>
      <rect x="36" y="28" width="8" height="8" rx="1" fill="#4a5a6a"/>
      <rect x="12" y="46" width="30" height="2" rx="1" fill="#fff" opacity="0.7"/>
      <rect x="12" y="51" width="20" height="1.5" rx="0.75" fill="#fff" opacity="0.4"/>
      <rect x="44" y="44" width="20" height="8" rx="2" fill="#D4AF37" opacity="0.8"/>
      <rect x="46" y="47" width="16" height="2" rx="1" fill="#fff" opacity="0.9"/>
      <rect x="12" y="56" width="56" height="1" rx="0.5" fill="#D4AF37" opacity="0.3"/>
      <rect x="12" y="60" width="15" height="2" rx="1" fill="#D4AF37" opacity="0.5"/>
    </svg>
  );
}

function YouTubeThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#0a0808"/>
      <rect x="8" y="14" width="64" height="36" rx="4" fill="#1a0f0f"/>
      <circle cx="40" cy="32" r="12" fill="#FF0000" opacity="0.85"/>
      <polygon points="37,27 37,37 47,32" fill="white"/>
      <rect x="8" y="54" width="64" height="18" rx="4" fill="#1a0f0f"/>
      <rect x="12" y="58" width="40" height="3" rx="1.5" fill="#fff" opacity="0.8"/>
      <rect x="12" y="63" width="28" height="2" rx="1" fill="#fff" opacity="0.4"/>
      <rect x="56" y="57" width="12" height="6" rx="2" fill="#FF0000" opacity="0.7"/>
    </svg>
  );
}

function WeddingThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#0f0a14"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#1a1020"/>
      <rect x="12" y="12" width="56" height="56" rx="4" fill="#1e1428"/>
      <rect x="16" y="16" width="48" height="48" rx="3" stroke="#D4AF37" strokeWidth="0.5" strokeDasharray="2 2" fill="none"/>
      <path d="M34 28 Q40 22 46 28 Q40 34 34 28Z" fill="#D4AF37" opacity="0.5"/>
      <rect x="24" y="40" width="32" height="3" rx="1.5" fill="#fff" opacity="0.8"/>
      <rect x="28" y="46" width="24" height="2" rx="1" fill="#D4AF37" opacity="0.6"/>
      <rect x="30" y="51" width="20" height="2" rx="1" fill="#fff" opacity="0.4"/>
      <rect x="32" y="57" width="16" height="5" rx="2.5" fill="#D4AF37" opacity="0.3"/>
    </svg>
  );
}

function BabyThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#0a0f14"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#0f1a20"/>
      <circle cx="40" cy="30" r="16" fill="#93C5FD" opacity="0.15"/>
      <circle cx="40" cy="30" r="10" fill="#93C5FD" opacity="0.25"/>
      <circle cx="40" cy="30" r="6" fill="#93C5FD" opacity="0.5"/>
      <circle cx="37" cy="28" r="1.5" fill="#fff" opacity="0.7"/>
      <circle cx="43" cy="28" r="1.5" fill="#fff" opacity="0.7"/>
      <path d="M37 33 Q40 36 43 33" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.7"/>
      <rect x="20" y="50" width="40" height="3" rx="1.5" fill="#93C5FD" opacity="0.6"/>
      <rect x="24" y="56" width="32" height="2" rx="1" fill="#fff" opacity="0.4"/>
      <rect x="28" y="61" width="24" height="5" rx="2.5" fill="#D4AF37" opacity="0.5"/>
    </svg>
  );
}

function BusinessPosterThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#080808"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#111"/>
      <rect x="8" y="8" width="64" height="30" rx="6" fill="#1a1a0a"/>
      <rect x="12" y="12" width="56" height="22" rx="3" fill="#D4AF37" opacity="0.12"/>
      <rect x="16" y="16" width="40" height="4" rx="2" fill="#D4AF37" opacity="0.7"/>
      <rect x="16" y="22" width="28" height="2.5" rx="1.25" fill="#fff" opacity="0.5"/>
      <rect x="12" y="42" width="56" height="1" rx="0.5" fill="#D4AF37" opacity="0.4"/>
      <rect x="12" y="46" width="30" height="2.5" rx="1.25" fill="#fff" opacity="0.6"/>
      <rect x="12" y="51" width="22" height="2" rx="1" fill="#fff" opacity="0.35"/>
      <rect x="12" y="56" width="22" height="2" rx="1" fill="#fff" opacity="0.35"/>
      <rect x="44" y="44" width="20" height="16" rx="3" fill="#D4AF37" opacity="0.15"/>
      <rect x="46" y="48" width="16" height="2" rx="1" fill="#D4AF37" opacity="0.6"/>
    </svg>
  );
}

function GymThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#0a0a14"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#111120"/>
      <rect x="10" y="34" width="60" height="12" rx="3" fill="#6366F1" opacity="0.3"/>
      <rect x="10" y="36" width="14" height="8" rx="2" fill="#6366F1" opacity="0.6"/>
      <rect x="56" y="36" width="14" height="8" rx="2" fill="#6366F1" opacity="0.6"/>
      <rect x="24" y="38" width="32" height="4" rx="2" fill="#6366F1" opacity="0.8"/>
      <rect x="16" y="54" width="48" height="3" rx="1.5" fill="#fff" opacity="0.7"/>
      <rect x="20" y="60" width="40" height="2" rx="1" fill="#fff" opacity="0.4"/>
    </svg>
  );
}

function CafeThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#0f0a06"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#1a1208"/>
      <circle cx="40" cy="32" r="16" fill="#92400E" opacity="0.3"/>
      <circle cx="40" cy="32" r="10" fill="#92400E" opacity="0.5"/>
      <path d="M34 32 Q40 26 46 32" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.8"/>
      <rect x="30" y="44" width="20" height="4" rx="2" fill="#92400E" opacity="0.6"/>
      <rect x="16" y="54" width="48" height="3" rx="1.5" fill="#D4AF37" opacity="0.6"/>
      <rect x="22" y="60" width="36" height="2" rx="1" fill="#fff" opacity="0.4"/>
    </svg>
  );
}

function BirthdayThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#0f0814"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#1a1020"/>
      <rect x="20" y="44" width="40" height="24" rx="4" fill="#7C3AED" opacity="0.4"/>
      <rect x="24" y="48" width="32" height="16" rx="2" fill="#7C3AED" opacity="0.3"/>
      <rect x="20" y="40" width="40" height="6" rx="2" fill="#D4AF37" opacity="0.6"/>
      <rect x="36" y="28" width="8" height="14" rx="2" fill="#7C3AED" opacity="0.5"/>
      <circle cx="40" cy="26" r="3" fill="#F59E0B" opacity="0.8"/>
      <rect x="16" y="14" width="48" height="3" rx="1.5" fill="#fff" opacity="0.7"/>
      <rect x="22" y="20" width="36" height="2" rx="1" fill="#D4AF37" opacity="0.5"/>
    </svg>
  );
}

function GraduationThumb() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#080e14"/>
      <rect x="8" y="8" width="64" height="64" rx="6" fill="#0f1a20"/>
      <polygon points="40,20 60,30 40,40 20,30" fill="#D4AF37" opacity="0.5"/>
      <rect x="36" y="40" width="8" height="16" rx="1" fill="#D4AF37" opacity="0.4"/>
      <rect x="28" y="54" width="24" height="6" rx="3" fill="#D4AF37" opacity="0.3"/>
      <rect x="16" y="14" width="48" height="3" rx="1.5" fill="#fff" opacity="0.7"/>
      <rect x="22" y="20" width="36" height="2" rx="1" fill="#fff" opacity="0.4"/>
    </svg>
  );
}

// Static fallback thumbnail for categories without custom SVG
function DefaultThumb({ color = '#D4AF37' }: { color?: string }) {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="8" fill="#111"/>
      <rect x="12" y="12" width="56" height="36" rx="4" fill="#1a1a1a"/>
      <rect x="16" y="16" width="40" height="4" rx="2" fill={color} opacity="0.6"/>
      <rect x="16" y="24" width="28" height="2" rx="1" fill="#fff" opacity="0.4"/>
      <rect x="16" y="28" width="20" height="2" rx="1" fill="#fff" opacity="0.3"/>
      <rect x="12" y="54" width="56" height="18" rx="4" fill="#1a1a1a"/>
      <rect x="16" y="58" width="48" height="2" rx="1" fill="#fff" opacity="0.5"/>
      <rect x="16" y="63" width="32" height="2" rx="1" fill={color} opacity="0.4"/>
    </svg>
  );
}

const THUMB_MAP: Record<string, React.ComponentType> = {
  restaurant: RestaurantThumb,
  pizzeria: RestaurantThumb,
  cafe: CafeThumb,
  bakery: CafeThumb,
  'nail-studio': NailStudioThumb,
  'hair-salon': HairSalonThumb,
  barber: HairSalonThumb,
  'real-estate': RealEstateThumb,
  'youtube-creator': YouTubeThumb,
  wedding: WeddingThumb,
  'baby-announcement': BabyThumb,
  'baby-shower': BabyThumb,
  'baby-milestone': BabyThumb,
  'birthday-party': BirthdayThumb,
  'childrens-birthday': BirthdayThumb,
  graduation: GraduationThumb,
  gym: GymThumb,
  'personal-trainer': GymThumb,
};

interface Category {
  id: string;
  slug: string;
  name: string;
  category_type: 'business' | 'personal';
  industry_group: string | null;
}

function CarouselRow({ items, label }: { items: Category[]; label: string }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, items]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 144 + 12; // card width + gap
    const visibleCards = Math.floor(el.clientWidth / cardWidth);
    el.scrollBy({ left: dir === 'right' ? cardWidth * visibleCards : -(cardWidth * visibleCards), behavior: 'smooth' });
  };

  // Mouse drag
  const onMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft || 0);
    scrollLeft.current = scrollRef.current?.scrollLeft || 0;
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - (scrollRef.current.offsetLeft || 0);
    const walk = (x - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };
  const onMouseUp = () => { isDragging.current = false; };

  // Keyboard navigation
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); scroll('left'); }
    if (e.key === 'ArrowRight') { e.preventDefault(); scroll('right'); }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg sm:text-xl font-bold text-foreground">{label}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="w-8 h-8 rounded-full bg-surface-3 border border-border flex items-center justify-center text-slate-400 hover:text-foreground hover:border-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label={`Scroll ${label} left`}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="w-8 h-8 rounded-full bg-surface-3 border border-border flex items-center justify-center text-slate-400 hover:text-foreground hover:border-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label={`Scroll ${label} right`}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 cursor-grab active:cursor-grabbing select-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="region"
        aria-label={`${label} carousel`}
      >
        {items.map((item) => {
          const ThumbComponent = THUMB_MAP[item.slug];
          return (
            <Link
              key={`cat-${item.id}`}
              href={`/categories/${item.slug}`}
              className="flex-shrink-0 flex flex-col items-center gap-2 p-3 w-32 sm:w-36 rounded-2xl bg-card border border-border hover:border-primary/30 hover:bg-primary/5 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/40"
              draggable={false}
            >
              <div className="w-full aspect-square rounded-xl overflow-hidden bg-surface-3 flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                {ThumbComponent ? (
                  <ThumbComponent />
                ) : (
                  <DefaultThumb />
                )}
              </div>
              <span className="text-xs font-semibold text-foreground text-center leading-tight">{item.name}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function CategoryCarousels() {
  const [businessCategories, setBusinessCategories] = useState<Category[]>([]);
  const [personalCategories, setPersonalCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('categories')
          .select('id, slug, name, category_type, industry_group')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (error) throw error;

        const biz = (data || []).filter((c) => c.category_type === 'business');
        const personal = (data || []).filter((c) => c.category_type === 'personal');
        setBusinessCategories(biz);
        setPersonalCategories(personal);
      } catch {
        // Fallback to empty arrays — no fake data
        setBusinessCategories([]);
        setPersonalCategories([]);
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  if (loading) {
    return (
      <section className="py-10 px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
        <div className="h-8 w-48 bg-surface-3 rounded-lg animate-pulse mb-4" />
        <div className="flex gap-3 overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-32 sm:w-36 aspect-square rounded-2xl bg-surface-3 animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section id="categories" className="py-10 px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Browse by Category</h2>
        <p className="text-slate-300 text-sm sm:text-base">
          Select your industry or occasion to get started
        </p>
      </div>

      {businessCategories.length > 0 && (
        <div id="business-categories">
          <CarouselRow items={businessCategories} label="Business Categories" />
        </div>
      )}

      {personalCategories.length > 0 && (
        <div id="personal-categories">
          <CarouselRow items={personalCategories} label="Personal & Events" />
        </div>
      )}

      {businessCategories.length === 0 && personalCategories.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">Categories are being set up. Please check back shortly.</p>
        </div>
      )}
    </section>
  );
}